import {
  Clock,
  Users,
  MapPin,
  DollarSign,
  Calendar,
  Sun,
  Award,
  GraduationCap,
  MessageCircle,
  FileText,
  Target,
  CheckCircle2,
  Smartphone,
} from 'lucide-react';
import Year3FoundationsTemplate, {
  type Year3FoundationsPageData,
} from './templates/Year3FoundationsTemplate';

const PAGE: Year3FoundationsPageData = {
  route: '/programs/nsw/year-7-foundations-maths-tutoring',
  breadcrumb: ['NSW programs', 'Foundations', 'Year 7 Maths'],
  pill: 'Year 7 · NSW curriculum',
  h1: 'Year 7 Maths tutoring in NSW',
  subtitle:
    'Master algebra, fractions, geometry and reasoning in small groups - aligned to the NSW NESA Stage 4 mathematics syllabus, with Year 7 NAPLAN preparation built in.',
  quickFacts: [
    { icon: Clock, text: '{DURATION_SHORT}' },
    { icon: Users, text: '8-12 students' },
    { icon: MapPin, text: 'In-centre + online' },
    { icon: DollarSign, text: '{PRICE} / session' },
  ],
  courseDetailsHeading: 'Year 7 Maths',
  pricingHeading: 'Year 7 Maths',
  relatedHeading: 'More for Year 7 students',
  yearLevel: 7,
  tabs: [
    {
      id: 'term',
      icon: Calendar,
      name: 'Term course',
      sub: '10 weekly sessions per term',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Term Course overview',
          para:
            'The Year 7 Maths term course runs 10 sessions per term, {DURATION_LENGTH} each, in small groups of 8-12 students. Lessons are aligned to the NSW NESA Stage 4 Mathematics syllabus and build the algebraic and reasoning habits expected in high school.',
          bullets: [
            'Number and algebra, measurement and geometry, and statistics and probability covered across the term',
            'Content sequenced alongside the school year so tutoring and classroom work reinforce each other',
            'Strong preparation for Year 7 NAPLAN numeracy and a confident start to high school maths',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'This course is ideal for students who…',
          bullets: [
            'Are starting high school and want a strong foundation in algebra',
            'Are sitting Year 7 NAPLAN and want targeted numeracy preparation',
            'Aim for stronger performance in class tasks and assessments',
            'Are building problem-solving confidence through regular guided practice',
            '(Parents) want progress to be visible and reported each term',
          ],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para:
            'By the end of the term your child will have made measurable progress in:',
          bullets: [
            'Number - integers, fractions, decimals, percentages, ratio and rates',
            'Algebra - variables, expressions, linear equations and substitution',
            'Measurement and geometry - area, volume, angles, triangles and quadrilaterals',
            'NAPLAN and reasoning - multi-step problem solving under timed conditions',
          ],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'Topics are sequenced across the four school terms:',
          bullets: [
            'Term 1 - Number foundations: integers, fractions, decimals and percentages',
            'Term 2 - Introduction to algebra: expressions, substitution and linear equations',
            'Term 3 - Measurement, geometry and Year 7 NAPLAN numeracy preparation',
            'Term 4 - Statistics, probability and algebraic problem solving',
          ],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para:
            'Every session follows a consistent structure so students always know what to expect:',
          bullets: [
            'Free level assessment before the first session - no wasted lessons on work already mastered',
            'Each 60-min lesson: introduce the concept → worked examples → guided practice → independent problem solving',
            "Written and verbal feedback on every student's work each session",
            "End-of-term progress report so parents can see clearly what's improved",
          ],
        },
      ],
    },
    {
      id: 'holiday',
      icon: Sun,
      name: 'Holiday intensive',
      sub: '4 daily lessons in the holidays',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Holiday intensive overview',
          para:
            'The Year 7 Maths holiday intensive runs 4 daily sessions over the school holidays, 60 minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.',
          bullets: [
            '4 sessions, daily, 60 minutes each',
            'One maths strand - deep focus rather than broad coverage',
            'Same small groups and qualified educators as the term course',
            'No prior Schoolola enrolment needed',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'The holiday intensive suits students who…',
          bullets: [
            'Are preparing for NAPLAN or upcoming assessments',
            'Want a focused short boost before the next school term begins',
            "Can't commit to a full term but want structured, quality tuition",
            'Need to build confidence in one specific maths topic quickly',
          ],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para: 'After 4 sessions your child will have:',
          bullets: [
            'A solid understanding of one strand - concepts, strategies and worked examples',
            'Numeracy strategies for NAPLAN and high school assessments under timed conditions',
            'Increased confidence to tackle maths tasks at school',
            'Specific written feedback on their own practice problems',
          ],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'The four sessions follow a structured daily sequence:',
          bullets: [
            'Day 1 - Strand overview: key concepts, worked examples, mental strategies',
            'Day 2 - Guided practice: scaffold problems, vocabulary and notation',
            'Day 3 - Problem solving: multi-step questions, reasoning and checking',
            'Day 4 - Practice task under assessment conditions with full written feedback',
          ],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para:
            'The intensive is designed for maximum impact across four consecutive sessions:',
          bullets: [
            'Register your interest - sessions fill quickly in school holiday periods',
            'Each day builds on the previous - daily attendance is expected',
            'Individual written feedback on Day 3 and Day 4 tasks included',
            'Students leave with a completed problem-solving portfolio',
          ],
        },
      ],
    },
  ],
  inclusions: [
    {
      icon: Award,
      name: 'Qualified educators',
      desc: 'Teachers with NSW curriculum expertise and classroom experience - consistent across all NSW centres.',
    },
    {
      icon: Users,
      name: 'Small group classes',
      desc: '8-12 students per class. Your child gets seen, heard and responded to every lesson.',
    },
    {
      icon: GraduationCap,
      name: 'NSW curriculum-aligned',
      desc: 'Content mapped directly to NESA Stage 4 outcomes. Complements school, session by session.',
    },
    {
      icon: MessageCircle,
      name: 'Feedback every lesson',
      desc: "Structured written and verbal feedback on every student's work, every session.",
    },
    {
      icon: FileText,
      name: 'End-of-term report',
      desc: 'A clear parent-facing progress summary at end of each term - before school reports arrive.',
    },
    {
      icon: Target,
      name: 'Free assessment',
      desc: 'Level placement before the first session. No wasted lessons on work already mastered.',
    },
    {
      icon: CheckCircle2,
      name: 'NAPLAN & high school ready',
      desc: 'Year 7 content directly supports Year 7 NAPLAN numeracy and builds the algebraic habits required across Stage 4 maths.',
    },
    {
      icon: Smartphone,
      name: 'Schoolola LMS',
      desc: 'Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.',
      images: [
        '/lovable-uploads/lms-parent-dashboard.png',
        '/lovable-uploads/lms-learning-dashboard.png',
        '/lovable-uploads/lms-diagnostic-overview.png',
        '/lovable-uploads/lms-quiz-question-1.png',
        '/lovable-uploads/lms-quiz-question-2.png',
        '/lovable-uploads/lms-results-report.png',
      ],
    },
  ],
  faq: [
    {
      q: 'Is {PRICE} the full price - are there any extras?',
      a: '{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}',
    },
    {
      q: 'What study materials are provided?',
      a: 'Study materials are provided as bound books - no loose papers. {BOOKS}',
    },
    {
      q: 'How is this different from what happens at school?',
      a: "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same NESA Stage 4 outcomes - we're giving your child more practice and more attention within the same curriculum.",
    },
    {
      q: 'What if my child misses a session?',
      a: 'We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre.',
    },
    {
      q: 'Can a confident Year 7 student still be challenged?',
      a: 'Yes. The assessment places students within ability groups, not just year groups. A Year 7 student working above level will be placed in a group that challenges them accordingly.',
    },
  ],
  related: [
    {
      tag: 'Year 7 · NSW',
      name: 'Year 7 English',
      desc: 'Analytical reading, writing and grammar',
      href: '/programs/nsw/year-7-foundations-english-tutoring',
    },
    {
      tag: 'Year 7-8 · NSW',
      name: 'Scholarship Exam Course',
      desc: 'Private school scholarship preparation',
      href: '/programs/scholarship-exam-course',
    },
  ],
  seo: {
    title: 'Year 7 Maths Tutoring NSW - Small Groups | Schoolola',
    description:
      'Year 7 Maths tutoring in NSW. NESA Stage 4 aligned algebra, fractions, geometry and reasoning with Year 7 NAPLAN readiness. Small groups of 8-12 students..',
    schemaCourseName: 'Year 7 Maths Tutoring - NSW',
    schemaDescription:
      'Year 7 Maths tuition in NSW. Small groups of 8-12 students, aligned to NESA Stage 4. Algebra, fractions, geometry, reasoning and Year 7 NAPLAN readiness..',
  },
};

export default function NswYear7FoundationsMaths() {
  return <Year3FoundationsTemplate page={PAGE} />;
}
